import { File } from "buffer";
import RequisicaoUtilitario, { BasicQueryType, MutationBasicaParam, QueryBasicaParam } from "../../../Utils/RequisicaoUtilitario";
import { AxiosResponse } from "axios";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Axios } from "../../Axios";
import { TypeInscricaoDoAlunoNaViagem } from "../../../Models/Domain/Viagem/InscricaoDoAlunoNaViagem";
import { queryClient } from "../../../AreaRestritta/AppAreaRestrita";
import dayjs from "dayjs";
import { EnumContaProvedorConciliacao } from "@/Models/Financeiro/Conta";

export function useObterMinutaInscricao({
    codigo,
    ...rest
}:BasicQueryType<AxiosResponse<Blob|null>, { codigo:number }>) {
    return useQuery({
        queryKey : ["Domain.Viagem.Inscricao.Minuta", `Domain.Viagem.Inscricao.${codigo}.Minuta`],
        queryFn : () => Axios.get<Blob|null>(`restrita/viagem/Inscricao/${codigo}/minuta`, { responseType : "blob" })
    });
}

export function useInscreverNovoAluno({
    onSuccess,
    ...rest
}:MutationBasicaParam<TypeInscricaoDoAlunoNaViagem, AxiosResponse<TypeInscricaoDoAlunoNaViagem>>) {
    return useMutation({
        ...rest,
        mutationFn : (inscricao) => (inscricao.aluno?.codigo ?? 0) > 0 ? Axios.post<TypeInscricaoDoAlunoNaViagem>("/restrita/viagem/inscricao", inscricao) : Axios.post<TypeInscricaoDoAlunoNaViagem>("/restrita/viagem/inscricao/novo-aluno", inscricao),
        onSuccess(data, variables, context) {
            queryClient.invalidateQueries({ queryKey : ["Pessoa.AlunoResponsavel.Responsavel.ObterAlunosResponsavel"] });
            queryClient.invalidateQueries({ queryKey : ["Domain", "Restrita", "Viagem", "Inscricao"] });

            onSuccess?.(data, variables, context);
        }
    });
}

export function useSalvarUltimoPassoInscricao({
    ...rest
}:MutationBasicaParam<TypeInscricaoDoAlunoNaViagem, AxiosResponse<void>>) {
    return useMutation({
        ...rest,
        mutationFn : (inscricao) => Axios.patch<void>(`quiron/viagem/${inscricao.codViagem}/aluno/${inscricao.codAluno}/alterarPasso`, inscricao)
    });
}

export function useObterInscricaoViagemAluno({
    codViagem,
    codAluno,
    onSuccess,
    ...rest
}:QueryBasicaParam<AxiosResponse<TypeInscricaoDoAlunoNaViagem|null>, TypeInscricaoDoAlunoNaViagem|null, { codViagem : number, codAluno : number, onSuccess ?: (data: TypeInscricaoDoAlunoNaViagem|null) => void }>) {
    return useQuery({
        ...rest,
        queryKey: [ "Domain", "Restrita", "Viagem", "Inscricao", codViagem, "Aluno", codAluno, "Buscar" ],
        queryFn: () => {
            return Axios.get<TypeInscricaoDoAlunoNaViagem|null>(`quiron/viagem/${codViagem}/aluno/${codAluno}`).then((resp) => {
                onSuccess?.(resp.data);
                return resp;
            });
        },
        select(response) {
            if (response.data?.viagem?.inicio != null)
                response.data.viagem.inicio = new Date(response.data.viagem.inicio as any);
            return response.data;
        },
        staleTime: 0
    });
}

export function useListarInscricoesResponsavelSessao({
    ...rest
}:QueryBasicaParam<AxiosResponse<TypeInscricaoDoAlunoNaViagem[]>, TypeInscricaoDoAlunoNaViagem[]>) {
    return useQuery({
        ...rest,
        queryKey: ["Domain.Restrita.Viagem.Inscricao.Responsavel"],
        queryFn: () => Axios.get<TypeInscricaoDoAlunoNaViagem[]>(`/restrita/viagem/inscricao/responsavel`),
        select(response) {
            return response.data;
        },
        staleTime: 0
    });
}


export function useListarInscricoesPorResponsavel({
    codResponsavel,
    codCentroDeCusto,
    ...rest
}:QueryBasicaParam<AxiosResponse<TypeInscricaoDoAlunoNaViagem[]>, TypeInscricaoDoAlunoNaViagem[], { codResponsavel : number, codCentroDeCusto?:number[]|null }>) {
    codCentroDeCusto = codCentroDeCusto ?? [];

    const queryString = RequisicaoUtilitario.objectToQueryString({
        codCentroDeCusto
    });

    return useQuery({
        ...rest,
        queryKey: [ "Domain", "Restrita", "Viagem", "Inscricao", "Responsavel", codResponsavel, `?${queryString}` ],
        queryFn: () => Axios.get<TypeInscricaoDoAlunoNaViagem[]>(`/restrita/viagem/inscricao/responsavel/${codResponsavel}?${queryString}`),
        select(response) {
            return response.data.map(m => {
                if (m.viagem?.inicio != null)
                    m.viagem.inicio = new Date(m.viagem?.inicio as any);
                
                return m;
            });
        },
        staleTime: 0
    });
}

export interface IGerarLinkPagamentoInscricaoParams {
    codInscricao : number;
    valorBase : number;
    quantidadeParcelas : number;
    dataVencimento ?: Date|dayjs.Dayjs;
    codProvedorConciliacao ?: EnumContaProvedorConciliacao;
    enviaEmail : boolean;
}
export function useGerarLinkPagamentoInscricao({
    ...rest
}:MutationBasicaParam<IGerarLinkPagamentoInscricaoParams, AxiosResponse<TypeInscricaoDoAlunoNaViagem>>) {
    return useMutation({
        ...rest,
        mutationFn : (entrada) => Axios.post<TypeInscricaoDoAlunoNaViagem>(`/restrita/viagem/inscricao/informacao-pagamento`, entrada)
    });
}

export interface IInscricaoAlterarProvedorParams {
    codInscricao : number;
    codProvedorConciliacao ?: EnumContaProvedorConciliacao;
}
export function useInscricaoAlterarProvedor({
    ...rest
}:MutationBasicaParam<IInscricaoAlterarProvedorParams, AxiosResponse<TypeInscricaoDoAlunoNaViagem>>) {
    return useMutation({
        ...rest,
        mutationFn : (entrada) => Axios.patch<TypeInscricaoDoAlunoNaViagem>(`/restrita/viagem/inscricao/altera-provedor`, entrada)
    });
}

export function useInscricaoBuscarPorCodigo({
    codInscricao,
    ...rest
}:QueryBasicaParam<AxiosResponse<TypeInscricaoDoAlunoNaViagem|undefined>, TypeInscricaoDoAlunoNaViagem|undefined, { codInscricao : number }>) {
    return useQuery({
        ...rest,
        queryKey: [ "Domain.Restrita.Viagem.Inscricao", `Domain.Restrita.Viagem.Inscricao.${codInscricao}` ],
        queryFn: () => Axios.get<TypeInscricaoDoAlunoNaViagem|undefined>(`/restrita/viagem/inscricao/${codInscricao}`),
        select(response) {
            if (response.data === undefined) return undefined;

            if (response.data.viagem !== undefined && response.data.viagem !== null)
            {
                response.data.viagem.inicio &&= new Date(response.data.viagem?.inicio as any);
                response.data.viagem.fim &&= new Date(response.data.viagem?.fim as any);
                response.data.viagem.prazoDeInscricao &&= new Date(response.data.viagem?.prazoDeInscricao as any);
            }

            return response.data;
        }
    });
}